import { useState, useCallback } from 'react';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { parseAbi, formatUnits, parseUnits, Abi, Address } from 'viem';

/**
 * Transaction state
 */
interface TransactionState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: string | null;
  txHash: string | null;
}

/**
 * useContractInteraction Hook - Easy Smart Contract Interaction
 *
 * Features:
 * - Read from contracts (view/pure functions)
 * - Write to contracts (state-changing functions)
 * - Automatic transaction status tracking
 * - Type-safe contract interactions
 * - Error handling
 *
 * Usage:
 * ```tsx
 * const { readContract, writeContract, txState } = useContractInteraction();
 *
 * // Read example
 * const balance = await readContract({
 *   address: '0x...',
 *   abi: tokenAbi,
 *   functionName: 'balanceOf',
 *   args: [userAddress],
 * });
 *
 * // Write example
 * await writeContract({
 *   address: '0x...',
 *   abi: tokenAbi,
 *   functionName: 'transfer',
 *   args: [recipientAddress, amount],
 * });
 * ```
 */
export function useContractInteraction() {
  const { address, chainId } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const [txState, setTxState] = useState<TransactionState>({
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
    txHash: null,
  });

  /**
   * Reset transaction state
   */
  const resetTxState = useCallback(() => {
    setTxState({
      isLoading: false,
      isSuccess: false,
      isError: false,
      error: null,
      txHash: null,
    });
  }, []);

  /**
   * Read from contract (view/pure functions)
   */
  const readContract = useCallback(
    async <T = unknown>({
      address: contractAddress,
      abi,
      functionName,
      args = [],
    }: {
      address: Address;
      abi: Abi | readonly string[];
      functionName: string;
      args?: readonly unknown[];
    }): Promise<T | null> => {
      if (!publicClient) {
        console.error('Public client not available');
        return null;
      }

      try {
        // Parse ABI if it's string array
        const parsedAbi = Array.isArray(abi) && typeof abi[0] === 'string'
          ? parseAbi(abi as readonly string[])
          : abi as Abi;

        const result = await publicClient.readContract({
          address: contractAddress,
          abi: parsedAbi,
          functionName,
          args,
        });

        return result as T;
      } catch (error: unknown) {
        console.error('Contract read error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to read from contract';
        throw new Error(errorMessage);
      }
    },
    [publicClient]
  );

  /**
   * Write to contract (state-changing functions)
   */
  const writeContract = useCallback(
    async ({
      address: contractAddress,
      abi,
      functionName,
      args = [],
      value,
    }: {
      address: Address;
      abi: Abi | readonly string[];
      functionName: string;
      args?: readonly unknown[];
      value?: bigint;
    }): Promise<string | null> => {
      if (!walletClient || !address) {
        const error = 'Wallet not connected';
        setTxState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error,
          txHash: null,
        });
        throw new Error(error);
      }

      if (!publicClient) {
        const error = 'Public client not available';
        setTxState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error,
          txHash: null,
        });
        throw new Error(error);
      }

      setTxState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        txHash: null,
      });

      try {
        // Parse ABI if it's string array
        const parsedAbi = Array.isArray(abi) && typeof abi[0] === 'string'
          ? parseAbi(abi as readonly string[])
          : abi as Abi;

        // Simulate contract call first to catch errors
        const { request } = await publicClient.simulateContract({
          account: address,
          address: contractAddress,
          abi: parsedAbi,
          functionName,
          args,
          value,
        });

        // Send transaction
        const txHash = await walletClient.writeContract(request);

        setTxState({
          isLoading: true,
          isSuccess: false,
          isError: false,
          error: null,
          txHash,
        });

        // Wait for transaction receipt
        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash,
        });

        const isSuccess = receipt.status === 'success';

        setTxState({
          isLoading: false,
          isSuccess,
          isError: !isSuccess,
          error: isSuccess ? null : 'Transaction failed',
          txHash,
        });

        return txHash;
      } catch (error: unknown) {
        console.error('Contract write error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Transaction failed';

        setTxState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
          txHash: null,
        });

        throw new Error(errorMessage);
      }
    },
    [walletClient, publicClient, address]
  );

  /**
   * Multi-call - Read multiple contract functions in a single call
   */
  const multicall = useCallback(
    async <T = unknown>({
      address: contractAddress,
      abi,
      calls,
    }: {
      address: Address;
      abi: Abi | readonly string[];
      calls: Array<{
        functionName: string;
        args?: readonly unknown[];
      }>;
    }): Promise<T[]> => {
      if (!publicClient) {
        throw new Error('Public client not available');
      }

      try {
        // Parse ABI if it's string array
        const parsedAbi = Array.isArray(abi) && typeof abi[0] === 'string'
          ? parseAbi(abi as readonly string[])
          : abi as Abi;

        const results = await Promise.all(
          calls.map(call =>
            publicClient.readContract({
              address: contractAddress,
              abi: parsedAbi,
              functionName: call.functionName,
              args: call.args || [],
            })
          )
        );

        return results as T[];
      } catch (error: unknown) {
        console.error('Multicall error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to execute multicall';
        throw new Error(errorMessage);
      }
    },
    [publicClient]
  );

  return {
    // Read operations
    readContract,
    multicall,

    // Write operations
    writeContract,

    // Transaction state
    txState,
    resetTxState,

    // Client status
    isReady: !!publicClient && !!walletClient && !!address,
    chainId,
    address,
  };
}

/**
 * Helper functions for common token operations
 */

/**
 * Parse token amount with decimals
 */
export function parseTokenAmount(amount: string, decimals: number = 18): bigint {
  return parseUnits(amount, decimals);
}

/**
 * Format token amount from wei
 */
export function formatTokenAmount(
  amount: bigint,
  decimals: number = 18,
  displayDecimals: number = 4
): string {
  const formatted = formatUnits(amount, decimals);
  const num = parseFloat(formatted);
  return num.toFixed(displayDecimals);
}

/**
 * Standard ERC20 token ABI (minimal)
 */
export const ERC20_ABI = parseAbi([
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) returns (bool)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
  'event Approval(address indexed owner, address indexed spender, uint256 value)',
]);

/**
 * Standard ERC721 NFT ABI (minimal)
 */
export const ERC721_ABI = parseAbi([
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function tokenURI(uint256 tokenId) view returns (string)',
  'function balanceOf(address owner) view returns (uint256)',
  'function ownerOf(uint256 tokenId) view returns (address)',
  'function approve(address to, uint256 tokenId)',
  'function getApproved(uint256 tokenId) view returns (address)',
  'function setApprovalForAll(address operator, bool approved)',
  'function isApprovedForAll(address owner, address operator) view returns (bool)',
  'function transferFrom(address from, address to, uint256 tokenId)',
  'function safeTransferFrom(address from, address to, uint256 tokenId)',
  'event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)',
  'event Approval(address indexed owner, address indexed approved, uint256 indexed tokenId)',
]);

export default useContractInteraction;
